import React from "react";
import { inventory } from "@foresting-tomorrow/validators";
import { FirestoreInventoryItemProps } from "types/auth";
import { FirestoreFileProps } from "types/files";
import type { AppConfigCompanyChoiceTypes, InventoryDataConfigProps } from "types/appFunctionality";

type ValidateAndInformInventoryProps = {
  inventoryTitle: string;
  variant: FirestoreInventoryItemProps["inventoryType"];
  data: any;
  fileDocs: FirestoreFileProps[];
  ignoredFileCategories?: string[];
  inventoryConfig?: Partial<Pick<InventoryDataConfigProps,
    "loggingCompanyType" | "forwardingCompanyType" | "chippingCompanyType"
  >>;
  translate: (key: string) => string;
};

export function getInventoryValidationSection(path: string, translate: (key: string) => string): string {
  const [field, certificationTag] = path.split(".");
  const section = translate(`snackbar-raw-wood-input-handling-section-inventory-${field}`);
  if (
    certificationTag &&
    (field === "certificationClaims" || field === "certificationNumbers")
  ) {
    return `${certificationTag.toUpperCase()}-${section}`;
  }
  return section;
}

export function validateAndInformInventory({
  inventoryTitle,
  variant,
  data,
  fileDocs,
  ignoredFileCategories = [],
  inventoryConfig,
  translate,
}: ValidateAndInformInventoryProps): { valid: boolean; errorMessage: JSX.Element | null } {
  if (!data) {
    return { valid: false, errorMessage: null };
  }
  const isRequired = (mode?: AppConfigCompanyChoiceTypes) =>
    mode !== "hidden" && mode !== "disabled";
  const result = inventory.validateInventoryDK({
    data: {
      variant: variant,
      ...data,
    },
    fileDocs: fileDocs,
    requirements: {
      loggingCompany: isRequired(inventoryConfig?.loggingCompanyType),
      forwardingCompany: isRequired(inventoryConfig?.forwardingCompanyType),
      chippingCompany: isRequired(inventoryConfig?.chippingCompanyType),
    },
  });
  const errors = result.errors.filter(
    (error) =>
      !ignoredFileCategories.some((category) => error.path === `files:${category}`),
  );
  if (errors.length === 0) {
    return { valid: true, errorMessage: null };
  }
  // Create html message listing errors
  const errorMessage = (
    <div style={{ display: "flex", flexDirection: "column" }}>
      {`${inventoryTitle} ${translate("snackbar-raw-wood-input-handling-transfer-failed")}`} <br />
      <br />
      <strong>{`${inventoryTitle}:`}</strong>
      {errors
        .sort((a, b) => a.path.localeCompare(b.path))
        .map((err, index) => {
          // Get indexing for list
          const listIndex = index + 1;
          // Get section from path to translate
          const section = getInventoryValidationSection(err.path, translate);
          let missingText = translate("snackbar-raw-wood-input-handling-missing-item");
          if (err.path.includes("files")) {
            missingText = translate("snackbar-raw-wood-input-handling-missing-file");
          }
          return (
            <React.Fragment key={index}>
              {`${listIndex}. ${section} ${missingText}`} <br />
            </React.Fragment>
          );
        })}
    </div>
  );

  return {
    valid: false,
    errorMessage,
  };
}
